import styled, { css } from 'styled-components';
import { formatDatetime, formatDateToNow } from 'app/shared/utils/date';
import { Narrative } from 'app/modules/narratives/models';
import {
  U21Button,
  U21Grid,
  U21Typography,
  U21Spacer,
  U21Avatar,
} from 'app/shared/u21-ui/components';
import { IconClipboardList, IconTrash } from '@u21/tabler-icons';
import { byAgentNameHelper } from 'app/modules/narratives/utils';
import { AI_COLORS } from 'app/modules/ai/constants';
import { alpha } from '@mui/system/colorManipulator';
import { InvestigationType } from 'app/modules/investigations/models';
import { QueueType } from 'app/modules/queues/models';
import { useGetDefaultQueue } from 'app/modules/queues/queries/useGetDefaultQueue';
import { IconAiAgent } from 'app/shared/components/Icons/IconAiAgent';

interface NarrativeHeaderProps {
  narrative: Narrative;
  isSidePanel?: boolean;
  isReadOnly?: boolean;
  onDelete?: () => void;
  investigationType: InvestigationType;
}

export const NarrativeHeader = ({
  narrative,
  isSidePanel = false,
  isReadOnly = false,
  onDelete,
  investigationType,
}: NarrativeHeaderProps) => {
  const {
    created_by: createdBy,
    edited_by: editedBy,
    created_at: createdAt,
    updated_at: updatedAt,
    added_to_record_by: addedToRecordBy,
    is_automated: isAutomated,
    queue,
  } = narrative;

  const defaultQueue = useGetDefaultQueue(
    investigationType === InvestigationType.ALERT
      ? QueueType.ALERT_QUEUE
      : QueueType.CASE_QUEUE,
  );

  const queueTitle = queue?.title || defaultQueue?.title;

  if (isSidePanel) {
    return (
      <NarrativeHeaderSidePanel $isAutomated={isAutomated}>
        <HeaderContent>
          <U21Typography
            ellipsis
            icon={isAutomated ? <IconAiAgent /> : <IconClipboardList />}
            variant="subtitle1"
          >
            {isAutomated ? 'AI Narrative' : queueTitle || ''}
          </U21Typography>
          {!isReadOnly && onDelete && (
            <U21Button
              aria-label="Delete Narrative"
              icon={<IconTrash />}
              onClick={onDelete}
            />
          )}
        </HeaderContent>
        <StyledU21Grid minWidth={200}>
          <TimestampContent>
            <U21Typography variant="body2">
              {`Created ${byAgentNameHelper(createdBy)}`.trim()}
            </U21Typography>
            <DateTypography
              color="text.secondary"
              tooltip={formatDatetime(createdAt)}
              variant="caption"
            >
              {formatDateToNow(createdAt, { addSuffix: true })}
            </DateTypography>
          </TimestampContent>
          {(addedToRecordBy.id > 0 || editedBy.id > 0) && (
            <TimestampContent>
              {addedToRecordBy.id > 0 ? (
                <U21Typography variant="body2">
                  {`Added to record ${byAgentNameHelper(addedToRecordBy)}`.trim()}
                </U21Typography>
              ) : (
                <U21Typography variant="body2">
                  {`Edited ${byAgentNameHelper(editedBy)}`.trim()}
                </U21Typography>
              )}
              <DateTypography
                color="text.secondary"
                tooltip={formatDatetime(updatedAt)}
                variant="caption"
              >
                {formatDateToNow(updatedAt, { addSuffix: true })}
              </DateTypography>
            </TimestampContent>
          )}
        </StyledU21Grid>
      </NarrativeHeaderSidePanel>
    );
  }

  return (
    <NarrativeHeaderContainer>
      <U21Spacer align="start" horizontal>
        <U21Avatar name={createdBy.full_name} src={createdBy.picture} />
        <U21Spacer>
          <IconStyledTypography variant="subtitle1" color="text.secondary">
            {`Created ${byAgentNameHelper(createdBy)} on ${formatDatetime(
              new Date(createdAt),
            )}`}
            <StyledIconClipboardList />
            <StyledTypography variant="subtitle1" color="text.secondary">
              {queueTitle || ''}
            </StyledTypography>
          </IconStyledTypography>
          <StyledTypography variant="subtitle2" color="text.secondary">
            {addedToRecordBy.id > 0
              ? `Added to record ${byAgentNameHelper(addedToRecordBy)} on ${formatDatetime(
                  new Date(updatedAt),
                )}`
              : `Edited ${byAgentNameHelper(editedBy)} on ${formatDatetime(
                  new Date(updatedAt),
                )}`}
          </StyledTypography>
        </U21Spacer>
      </U21Spacer>
      {!isReadOnly && (
        <U21Button
          aria-label="Delete Narrative"
          icon={<IconTrash />}
          onClick={onDelete}
        />
      )}
    </NarrativeHeaderContainer>
  );
};

const NarrativeHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  border-radius: 8px 8px 0 0;
  background-color: ${(props) => props.theme.palette.grey[500_16]};

  ${(props) => {
    const greyStyle = `1px solid ${props.theme.palette.grey[500_24]}`;
    return css`
      border-top: ${greyStyle};
      border-left: ${greyStyle};
      border-right: ${greyStyle};
    `;
  }}
`;

const NarrativeHeaderSidePanel = styled.div<{ $isAutomated?: boolean }>`
  padding: 16px;
  border-radius: 8px 8px 0 0;
  background-color: ${(props) =>
    props.$isAutomated
      ? alpha(AI_COLORS.main, 0.08)
      : props.theme.palette.background.neutral};
  ${(props) => {
    const greyStyle = `1px solid ${props.theme.palette.grey[500_24]}`;
    return css`
      border-top: ${greyStyle};
      border-left: ${greyStyle};
      border-right: ${greyStyle};
    `;
  }}
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledU21Grid = styled(U21Grid)`
  gap: 0 8px;
`;

const TimestampContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0 4px;
`;

const DateTypography = styled(U21Typography)`
  &:hover {
    text-decoration: underline;
    text-decoration-color: ${(props) => props.theme.palette.text.secondary};
  }
`;

const StyledTypography = styled(U21Typography)`
  margin-top: 0 !important;
`;

const IconStyledTypography = styled(U21Typography)`
  .MuiTypography-root {
    display: flex;
    align-items: center;
  }
`;

const StyledIconClipboardList = styled(IconClipboardList)`
  margin-left: 32px;
`;
